import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';
import copyToClipboard from 'copy-to-clipboard';
import capitalize from 'common/util/capitalize';

import { getHouseholdPets, calculateHouseholdDepositFees } from 'common/householdHelpers';

import {
  createFormFieldsByIdGetter,
  transformStringToBoolean,
  transformBooleanToYesNo,
} from 'client/helpers/formikHelpers';

import {
  getLeaseDraftingFieldDefinitions,
  withoutCheckboxInterpolation,
  getRecipientsList,
} from 'common/leaseDrafting/leaseDraftingFields';

import getNodeText from 'client/helpers/getNodeText';

import SrCard from 'client/components/SrCard/SrCard';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';
import SrFilterCheckbox from 'client/components/SrFilterCheckbox';
import SrLeaseDraftCheckbox from 'client/components/SrLeaseDraftHelper/SrLeaseDraftCheckbox';

const interpolateStringCheckboxes = (string) => {
  if (!string || typeof string !== 'string' || !string.includes('{*}')) {
    return string;
  }
  const stringParts = string.split('{*}');
  return (
    <>
      {stringParts.map((stringPart, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index}>
          {stringPart}
          {index < stringParts.length - 1 && <SrLeaseDraftCheckbox />}
        </span>
      ))}
    </>
  );
};

const getSections = ({ leaseDraftConfiguration, hideInputs, onHideInputsClick }) => {
  const fields = getLeaseDraftingFieldDefinitions(leaseDraftConfiguration);
  const getFieldById = createFormFieldsByIdGetter(fields);
  let sections = [
    {
      title: 'Get Started',
      fontSize: '16px',
      fields: [
        getFieldById('propertyType'),
        getFieldById('partner'),
        getFieldById('notesFromPartner'),
        // Injecting the hide default inputs toggle here, but not adding it to the main
        // fields array, because it is not really a field.
        {
          title: 'Hide default inputs',
          enableClickToCopy: false,
          value: (
            <SrBox paddingTop="5px">
              <SrFilterCheckbox
                id="hideDefaultInputs"
                value={hideInputs}
                onChange={(id, newValue) => {
                  if (onHideInputsClick) {
                    onHideInputsClick(newValue);
                  }
                }}
              />
            </SrBox>
          ),
        },
      ],
    },
    {
      title: 'Recipients',
      render: (leaseDraftInfo) => {
        const recipientsList = getRecipientsList({
          leaseDraftInfo,
          leaseDraftConfiguration,
        });

        return (
          <SrBox>
            {recipientsList.map((recipient) => (
              <SrBox
                key={recipient.title}
                padding="12px 16px 14px"
                borderRadius="14px"
                width="392px"
                border="2px solid rgba(123, 132, 136, 0.16)"
                marginTop="16px"
              >
                <SrText font="greycliff" weight="700" fontSize="18px">
                  {recipient.title}
                </SrText>
                <SrBox padding="16px 0 8px">
                  <SrFieldInfo title="Name">
                    {capitalize(recipient.name) || (
                      <SrText font="greycliff" weight="700" lineHeight="20px" color="red">
                        Missing Information
                      </SrText>
                    )}
                  </SrFieldInfo>
                </SrBox>
                <SrFieldInfo title="Email">
                  {recipient.email || (
                    <SrText font="greycliff" weight="700" lineHeight="20px" color="red">
                      Missing Information
                    </SrText>
                  )}
                </SrFieldInfo>
              </SrBox>
            ))}
          </SrBox>
        );
      },
    },
    {
      title: 'Documents',
      fontSize: '16px',
      fields: [
        getFieldById('documentsToInclude'),
        getFieldById('documentsToRemove'),
        getFieldById('partnerSubmittedDocs'),
      ],
    },
    {
      title: 'Parties',
      fields: [getFieldById('owner'), getFieldById('tenants')],
    },
    {
      title: 'Property',
      fields: [
        getFieldById('address'),
        getFieldById('addressUnitNumber'),
        getFieldById('legalDescription'),
        getFieldById('county'),
        getFieldById('appliances'),
      ],
    },
    {
      title: 'Term',
      fields: [
        getFieldById('leaseTerm'),
        getFieldById('commencementDate'),
        getFieldById('expirationDate'),
      ],
    },
    {
      title: 'Automatic Renewal',
      fields: [getFieldById('leaseRenewalNoticeTimeRange'), getFieldById('leaseRenewalNoticeDate')],
    },
    {
      title: 'Rent',
      fontSize: '16px',
      fields: [
        getFieldById('rent'),
        getFieldById('firstFullMonthDueDate'),
        getFieldById('collectionMethod'),
        getFieldById('tenantWillPayRentOn'),
        getFieldById('proratedRateOn'),
        getFieldById('proratedRentPrice'),
        getFieldById('proratedRentPlaceOfPayment'),
        getFieldById('proratedRentPlaceOfAddress'),
        getFieldById('methodOfPayment'),
      ],
    },
    {
      title: 'Late Charges',
      fontSize: '16px',
      fields: [
        getFieldById('lateChargeDay'),
        getFieldById('lateChargePrice'),
        getFieldById('lateChargeAdditionalDay'),
      ],
    },
    {
      title: 'Returned Payment',
      fontSize: '16px',
      fields: [getFieldById('returnedPaymentFee')],
    },
    {
      title: 'Pets',
      fontSize: '16px',
      fields: [getFieldById('petInitialViolationFee'), getFieldById('petDailyViolationFee')],
    },
    {
      title: 'Security Deposit',
      fontSize: '16px',
      fields: [getFieldById('securityDeposit'), getFieldById('securityDepositMethod')],
    },
    {
      title: 'Rent Concession',
      fontSize: '16px',
      fields: [getFieldById('concessionAmount'), getFieldById('concessionTerms')],
    },
    {
      title: 'Utilities',
      fontSize: '16px',
      fields: [getFieldById('landlordPaysFor')],
    },
    {
      title: 'Use and Occupancy',
      fontSize: '16px',
      fields: [getFieldById('tenantNameAges'), getFieldById('noGuestslongerThan')],
    },
    {
      title: 'Parking Rules',
      fontSize: '16px',
      fields: [getFieldById('parkingSpaces')],
    },
    {
      title: 'Access by Landlord',
      fontSize: '16px',
      fields: [
        getFieldById('landlordTripCharges'),
        getFieldById('authorizedPayKeyboxDuringLast'),
        getFieldById('withdrawKeyboxAuthorizationFee'),
      ],
    },
    {
      title: 'Move-in Condition',
      fontSize: '16px',
      fields: [
        getFieldById('landlordMakesNoPropertyWarranties'),
        getFieldById('tenantCompleteInventoryForm'),
      ],
    },
    {
      title: 'Property Maintenance',
      fontSize: '16px',
      fields: [
        getFieldById('whoWaterYard'),
        getFieldById('whoMaintainsYard'),
        getFieldById('smoking'),
      ],
    },
    {
      title: 'Repairs',
      fontSize: '16px',
      fields: [getFieldById('repairContact'), getFieldById('repairSpecificItem')],
    },
    {
      title: 'Special Provisions',
      fontSize: '16px',
      fields: [getFieldById('listOfProvisions')],
    },
    {
      title: 'Early Termination',
      fontSize: '16px',
      fields: [getFieldById('earlyTerminationA'), getFieldById('earlyTerminationB')],
    },
    {
      title: 'Addenda',
      fontSize: '16px',
      fields: [getFieldById('addendaDocumentsToInclude')],
    },
    {
      title: 'Notices',
      fontSize: '16px',
      fields: [
        getFieldById('tenants'),
        getFieldById('leadTenantEmail'),
        getFieldById('landlordName'),
        getFieldById('landlordEmail'),
      ],
    },
    {
      title: 'Information',
      fontSize: '16px',
      fields: [
        getFieldById('broker'),
        getFieldById('brokerCheckbox'),
        getFieldById('propertyManagedBy'),
        getFieldById('propertyManagerName'),
        getFieldById('propertyManagerPhone'),
        getFieldById('propertyManagerEmail'),
      ],
    },
    {
      title: 'Pet Agreement',
      render: ({ applications, listing, householdListing, concession }) => {
        const { pets, nonEsaPets } = getHouseholdPets({ householdApplications: applications });
        const {
          firstMonthRent,
          petDeposit,
          petFees,
          monthlyPetRent,
        } = calculateHouseholdDepositFees({
          pets: nonEsaPets,
          listing: {
            ...listing,
            collectFirstMonth: true, // hardcoding this so that we're calculating the first month rent and pet rent as well for display purposes
          },
          concession,
          householdListing,
        });
        const extraPetFields = lodashCompact([
          {
            title: 'Pet Deposit',
            value: petDeposit.toFixed(2),
          },
          {
            title: 'Monthly Rent + Pet Rent',
            value:
              `${firstMonthRent.toFixed(2)} + ${monthlyPetRent.toFixed(2)} = ` +
              `${(firstMonthRent + monthlyPetRent).toFixed(2)}`,
          },
          {
            title: 'Pet Fees',
            value: petFees.toFixed(2),
          },
          leaseDraftConfiguration?.fieldOverrides?.petSpecialProvisions && {
            title: 'Special Provisions',
            value: leaseDraftConfiguration.fieldOverrides.petSpecialProvisions,
          },
        ]);

        return (
          <>
            {pets.length === 0 ? (
              <SrBox padding="16px 0">
                <SrFieldInfo enableClickToCopy={false} title="Pets">
                  None.
                </SrFieldInfo>
              </SrBox>
            ) : (
              <SrBox>
                {pets.map((pet, index) => {
                  const petFields = [
                    {
                      title: 'Type',
                      value: pet.type,
                    },
                    {
                      title: 'Breed',
                      value: pet.breed,
                    },
                    {
                      title: 'Name',
                      value: capitalize(pet.name),
                    },
                    {
                      title: 'Color',
                      value: pet.color,
                    },
                    {
                      title: 'Weight',
                      value: pet.weight ? `${pet.weight}lbs` : '',
                    },
                    {
                      title: 'Age',
                      value: pet.age,
                    },
                    {
                      title: 'Gender',
                      value: capitalize(pet.gender),
                    },
                    {
                      title: 'Neutered?',
                      value: capitalize(transformBooleanToYesNo(pet.neutered)),
                    },
                    {
                      title: 'Declawed?',
                      value: capitalize(transformBooleanToYesNo(pet.declawed)),
                    },
                    {
                      title: 'Rabies Shots Current?',
                      value: capitalize(transformBooleanToYesNo(pet.rabiesShotsCurrent)),
                    },
                    {
                      title: 'Is ESA?',
                      value: capitalize(transformBooleanToYesNo(pet.isEsa)),
                    },
                  ];
                  return (
                    <SrBox
                      key={index} // eslint-disable-line react/no-array-index-key
                      padding="12px 16px"
                      borderRadius="14px"
                      width="392px"
                      border="2px solid rgba(123, 132, 136, 0.16)"
                      marginTop="16px"
                    >
                      <SrText font="greycliff" weight="700" fontSize="18px">
                        {`Pet ${index + 1}`}
                      </SrText>
                      <SrBox padding="16px 0">
                        {petFields.map(({ title, value }) => (
                          <SrBox
                            paddingTop="8px"
                            key={`pet-${index}-${title}`} // eslint-disable-line react/no-array-index-key
                          >
                            <SrFieldInfo title={title}>{value}</SrFieldInfo>
                          </SrBox>
                        ))}
                      </SrBox>
                    </SrBox>
                  );
                })}
                {extraPetFields.map(({ title, value }) => (
                  <SrBox key={title} padding="16px 0">
                    <SrFieldInfo title={title}>{value}</SrFieldInfo>
                  </SrBox>
                ))}
              </SrBox>
            )}
          </>
        );
      },
    },
  ];

  // Hide `hideInput` fields.
  if (hideInputs) {
    sections = sections.reduce((memo, section) => {
      const newSection = {
        ...section,
        fields: section.fields?.filter(({ hideWhenToggled }) => !hideWhenToggled),
      };
      if (newSection.render || newSection.fields?.length > 0) {
        memo.push(newSection);
      }
      return memo;
    }, []);
  }

  // Hide lease draft config "false" fields.
  if (leaseDraftConfiguration) {
    sections = sections.reduce((memo, section) => {
      const newSection = {
        ...section,
        fields: section.fields?.filter(({ value }) => transformStringToBoolean(value) !== false),
      };
      if (newSection.render || newSection.fields?.length > 0) {
        memo.push(newSection);
      }
      return memo;
    }, []);
  }

  return sections;
};

const SrFieldInfoText = ({ fontSize, color, children, enableClickToCopy }) => (
  <SrText
    font="greycliff"
    weight="700"
    fontSize={fontSize}
    lineHeight="20px"
    color={color || (children ? 'blueGreycliff' : 'red')}
    cursor={children && enableClickToCopy ? 'pointer' : null}
    onClick={() =>
      children && typeof children !== 'string' && enableClickToCopy
        ? copyToClipboard(withoutCheckboxInterpolation(getNodeText(children)))
        : null
    }
  >
    {!children
      ? 'Missing information'
      : typeof children === 'string'
      ? children.split('\n').map((childrenLine) => (
          <div
            key={childrenLine}
            onClick={() =>
              enableClickToCopy
                ? copyToClipboard(withoutCheckboxInterpolation(getNodeText(childrenLine)))
                : null
            }
          >
            {childrenLine}
          </div>
        ))
      : children}
  </SrText>
);

const SrFieldInfo = ({ title, fontSize, enableClickToCopy, children }) => (
  <>
    <SrText
      display="block"
      font="greycliff"
      weight="600"
      fontSize="14px"
      lineHeight="16.8px"
      color="blueGreycliff"
      opacity="0.6"
    >
      {title}
    </SrText>
    <SrFieldInfoText fontSize={fontSize} enableClickToCopy={enableClickToCopy}>
      {children}
    </SrFieldInfoText>
  </>
);

SrFieldInfo.propTypes = {
  title: PropTypes.string.isRequired,
  fontSize: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  enableClickToCopy: PropTypes.bool,
};

SrFieldInfo.defaultProps = {
  fontSize: '14px',
  enableClickToCopy: true,
};

SrFieldInfo.displayName = 'SrFieldInfo';

const SrLeaseDraftHelper = ({ leaseDraftInfo, leaseDraftConfiguration }) => {
  const [hideInputs, setHideInputs] = useState(true);
  const onHideInputsClick = useCallback(() => {
    setHideInputs(!hideInputs);
  }, [hideInputs]);

  const sections = getSections({
    leaseDraftConfiguration,
    hideInputs,
    onHideInputsClick,
  });

  return (
    <SrCard padding="0" maxWidth="100%" noContentPadding>
      {sections.map((section, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index}>
          <SrBox padding="20px">
            <SrText
              font="greycliff"
              weight="800"
              fontSize="24px"
              lineHeight="24px"
              paddingBottom="5px"
              display="block"
            >
              {section.title}
            </SrText>
            {section.render && section.render(leaseDraftInfo)}
            {section.fields &&
              section.fields.map((field, index) => {
                let fieldValue = interpolateStringCheckboxes(
                  typeof field.value === 'function' ? field.value(leaseDraftInfo) : field.value,
                );

                if (field.hideIfEmpty && !fieldValue) {
                  return null;
                }

                if (field.defaultValue && !fieldValue) {
                  fieldValue = field.defaultValue;
                }

                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <SrBox padding="16px 0" key={`${field.title}-${index}`}>
                    <SrFieldInfo
                      enableClickToCopy={field.enableClickToCopy}
                      fontSize={section.fontSize}
                      title={field.title}
                    >
                      {fieldValue}
                    </SrFieldInfo>
                  </SrBox>
                );
              })}
          </SrBox>
          <SrSeparator />
        </div>
      ))}
    </SrCard>
  );
};

SrLeaseDraftHelper.propTypes = {
  leaseDraftInfo: PropTypes.object.isRequired,
  leaseDraftConfiguration: PropTypes.object,
};

SrLeaseDraftHelper.displayName = 'SrLeaseDraftHelper';

export default SrLeaseDraftHelper;
